'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PartyPopper, ChevronRight, ArrowRight, Menu, Play } from 'lucide-react';
import type { CollegeCourseItem, CollegeChapterItem, CollegeStepItem } from '@zrt-ai-office/types/course';
import { ApiError } from '@/lib/api/client';
import { fetchCourseDetail, fetchCourseChapters, fetchChapterSteps, completeChapter } from '@/lib/academy/academy-api';
import { saveCourseProgress, getCourseProgress } from '@/lib/academy/learning-progress';
import { useAuthContext } from '@/lib/auth-context';
import { useToast } from '@/lib/hooks/use-toast';
import { LessonSidebar, LessonVideo, LessonArticle, toAcademySessionId } from '@/src/components/academy';

/* ── Skeleton loader ── */
function LessonSkeleton() {
	return (
		<div className="min-h-screen bg-slate-100">
			<aside className="fixed top-14 left-0 bottom-0 w-72 bg-white/95 border-r border-slate-200/50 hidden lg:block" style={{ zIndex: 30 }}>
				<div className="p-5 space-y-4">
					<div className="h-5 w-40 bg-slate-200 rounded animate-pulse" />
					<div className="space-y-2">
						<div className="flex justify-between">
							<div className="h-3 w-14 bg-slate-200 rounded animate-pulse" />
							<div className="h-3 w-8 bg-slate-200 rounded animate-pulse" />
						</div>
						<div className="h-1.5 w-full bg-slate-100 rounded-full" />
					</div>
					<div className="h-10 w-full bg-primary/10 rounded-xl animate-pulse" />
				</div>
				<div className="mx-4 h-px bg-slate-100" />
				<div className="px-3 py-3 space-y-1">
					{[1, 2, 3].map((i) => (
						<div className="flex items-center gap-3 px-3 py-3 rounded-lg" key={i}>
							<div className="h-5 w-5 bg-slate-200 rounded-full animate-pulse shrink-0" />
							<div className="flex-1 space-y-1.5">
								<div className="h-3.5 w-4/5 bg-slate-200 rounded animate-pulse" />
								<div className="h-2.5 w-3/5 bg-slate-100 rounded animate-pulse" />
							</div>
						</div>
					))}
				</div>
			</aside>
			<main className="lg:ml-72 pt-14">
				<div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 space-y-6">
					<div className="bg-white rounded-2xl p-6 sm:p-10 shadow-sm border border-slate-200/60 animate-pulse">
						<div className="flex items-center gap-4 mb-6">
							<div className="h-3.5 w-20 bg-primary/10 rounded" />
							<div className="h-3 w-24 bg-slate-100 rounded" />
						</div>
						<div className="h-8 w-3/4 bg-slate-200 rounded mb-4" />
						<div className="h-4 w-full bg-slate-100 rounded mb-3" />
						<div className="h-px w-full bg-slate-100 my-6" />
						<div className="space-y-3">
							<div className="h-4 w-full bg-slate-100 rounded" />
							<div className="h-4 w-11/12 bg-slate-100 rounded" />
							<div className="h-4 w-4/5 bg-slate-100 rounded" />
						</div>
					</div>
				</div>
			</main>
		</div>
	);
}

export function LessonPage() {
	const { courseId, chapterId } = useParams<{ courseId: string; chapterId: string }>();
	const navigate = useNavigate();
	const toast = useToast();
	const { isLoggedIn, openLogin } = useAuthContext();

	const [course, setCourse] = useState<CollegeCourseItem | null>(null);
	const [chapters, setChapters] = useState<CollegeChapterItem[]>([]);
	const [steps, setSteps] = useState<CollegeStepItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const numericChapterId = chapterId ? Number(chapterId) : null;

	/* ── Fetch course + chapters ── */
	useEffect(() => {
		if (!courseId) return;
		let cancelled = false;

		async function load() {
			setLoading(true);
			try {
				const [courseData, chaptersData] = await Promise.all([fetchCourseDetail(courseId!), fetchCourseChapters(courseId!)]);
				if (cancelled) return;
				setCourse(courseData);
				const sorted = [...chaptersData.list].sort((a: CollegeChapterItem, b: CollegeChapterItem) => a.sortOrder - b.sortOrder);
				setChapters(sorted);
			} catch (err) {
				if (!cancelled) {
					if (err instanceof ApiError && err.status === 401) {
						toast.info('请先登录才能查看课程内容');
						navigate(`/academy/${courseId}`);
						openLogin();
					} else {
						toast.error('课程加载失败');
					}
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		}

		load();
		return () => {
			cancelled = true;
		};
	}, [courseId, isLoggedIn]);

	/* ── Fetch steps when chapterId changes ── */
	useEffect(() => {
		if (!numericChapterId) {
			setSteps([]);
			return;
		}
		setSteps([]);
		setCurrentStepIndex(0);
		let cancelled = false;

		async function loadSteps() {
			try {
				const data = await fetchChapterSteps(numericChapterId!, courseId!);
				if (cancelled) return;
				setSteps(data.list);

				// Determine initial step: URL hash > localStorage > first step
				const hashStepId = window.location.hash.startsWith('#step-') ? Number(window.location.hash.slice(6)) : null;
				const hashIndex = hashStepId ? data.list.findIndex((s: CollegeStepItem) => s.id === hashStepId) : -1;
				if (hashIndex >= 0) {
					setCurrentStepIndex(hashIndex);
				} else {
					const saved = getCourseProgress(courseId!);
					if (saved && saved.chapterId === numericChapterId) {
						const savedIndex = data.list.findIndex((s: CollegeStepItem) => s.id === saved.stepId);
						if (savedIndex >= 0) setCurrentStepIndex(savedIndex);
					}
				}
			} catch (err) {
				if (!cancelled) {
					if (err instanceof ApiError && err.status === 401) {
						toast.info('请先登录才能查看课程内容');
						navigate(`/academy/${courseId}`);
						openLogin();
					}
					setSteps([]);
				}
			}
		}

		loadSteps();
		return () => {
			cancelled = true;
		};
	}, [numericChapterId, courseId, isLoggedIn]);

	/* ── Save progress when currentStepIndex changes ── */
	useEffect(() => {
		if (steps.length === 0 || !courseId || !numericChapterId) return;
		const step = steps[currentStepIndex];
		if (step) {
			saveCourseProgress(courseId, numericChapterId, step.id);
			// Update URL hash without triggering navigation
			const newHash = `#step-${step.id}`;
			if (window.location.hash !== newHash) {
				window.history.replaceState(null, '', newHash);
			}
		}
	}, [currentStepIndex, steps, courseId, numericChapterId]);

	/* ── Scroll to top when step changes ── */
	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [currentStepIndex]);

	/* ── Derived values ── */
	const activeChapter = useMemo(() => chapters.find((ch) => ch.id === numericChapterId) ?? null, [chapters, numericChapterId]);

	const allCompleted = chapters.length > 0 && chapters.every((ch) => ch.learned);

	const nextChapter = useMemo(() => {
		if (!activeChapter) return null;
		const sorted = [...chapters].sort((a, b) => a.sortOrder - b.sortOrder);
		const idx = sorted.findIndex((ch) => ch.id === activeChapter.id);
		if (idx < 0 || idx >= sorted.length - 1) return null;
		const next = sorted[idx + 1];
		return { id: next.id, title: next.title };
	}, [chapters, activeChapter]);

	const currentStep = steps[currentStepIndex] ?? null;
	const isLastStep = steps.length > 0 && currentStepIndex === steps.length - 1;

	const lessonIndex = activeChapter ? chapters.findIndex((ch) => ch.id === activeChapter.id) + 1 : 0;
	const lessonLabel = `第${lessonIndex}章`;

	/* ── Handlers ── */
	const handleSelectChapter = useCallback(
		(id: number) => {
			navigate(`/academy/${courseId}/${id}`);
			setMobileMenuOpen(false);
		},
		[courseId, navigate],
	);

	const handleSelectStep = useCallback(
		(stepId: number) => {
			const idx = steps.findIndex((s) => s.id === stepId);
			if (idx >= 0) {
				setCurrentStepIndex(idx);
			}
			setMobileMenuOpen(false);
		},
		[steps],
	);

	const handleGoToNextStep = useCallback(async () => {
		if (steps.length === 0) return;

		if (!isLastStep) {
			// Go to next step within the same chapter
			setCurrentStepIndex((prev) => prev + 1);
		} else if (nextChapter) {
			// Last step: complete chapter then go to next chapter's first step
			if (numericChapterId && courseId) {
				const activeCh = chapters.find((ch) => ch.id === numericChapterId);
				if (activeCh && !activeCh.learned) {
					try {
						await completeChapter(numericChapterId, courseId);
						const refreshed = await fetchCourseChapters(courseId);
						setChapters([...refreshed.list].sort((a, b) => a.sortOrder - b.sortOrder));
					} catch {
						// proceed anyway
					}
				}
			}
			navigate(`/academy/${courseId}/${nextChapter.id}`);
		} else {
			// Last step, no next chapter: just complete the chapter
			if (numericChapterId && courseId) {
				const activeCh = chapters.find((ch) => ch.id === numericChapterId);
				if (activeCh && !activeCh.learned) {
					try {
						await completeChapter(numericChapterId, courseId);
						const refreshed = await fetchCourseChapters(courseId);
						setChapters([...refreshed.list].sort((a, b) => a.sortOrder - b.sortOrder));
						toast.success('恭喜你完成了本课程的所有章节！');
					} catch {
						// ignore
					}
				}
			}
		}
	}, [isLastStep, nextChapter, steps, numericChapterId, courseId, chapters, navigate, toast]);

	/* ── Loading state ── */
	if (loading) {
		return <LessonSkeleton />;
	}

	return (
		<div className="min-h-screen bg-slate-100">
			<LessonSidebar
				activeChapterId={numericChapterId}
				activeStepId={currentStep?.id ?? null}
				allCompleted={allCompleted}
				chapters={chapters}
				courseTitle={course?.title ?? ''}
				mobileOpen={mobileMenuOpen}
				onBackToCourse={() => navigate(`/academy/${courseId}`)}
				onMobileClose={() => setMobileMenuOpen(false)}
				onSelectChapter={handleSelectChapter}
				onSelectStep={handleSelectStep}
				steps={steps}
			/>

			{/* ── Mobile sidebar toggle ── */}
			<div className="lg:hidden sticky top-14 z-20 border-b border-slate-200/50 bg-white/95 backdrop-blur-md">
				<button className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700" onClick={() => setMobileMenuOpen(true)} type="button">
					<Menu className="h-4 w-4" />
					<span className="truncate">{activeChapter ? `${lessonLabel}：${activeChapter.title}` : '选择章节'}</span>
				</button>
			</div>

			{/* ── Main content area ── */}
			<main className="lg:ml-72 pt-14">
				<div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
					{numericChapterId && activeChapter && currentStep ? (
						<>
							{/* ── Current step content ── */}
							{currentStep.contentType === 'video' ? (
								<LessonVideo title={currentStep.title} videoUrl={currentStep.content} />
							) : currentStep.contentType === 'md' ? (
								<LessonArticle
									content={currentStep.content}
									courseContext={{
										courseId: courseId ?? '',
										chapterId: chapterId ?? '',
										courseTitle: course?.title,
										chapterTitle: activeChapter?.title,
										chapterIntro: activeChapter?.intro,
										chapterObjective: activeChapter?.objective,
									}}
									lessonLabel={lessonLabel}
									readTime={currentStep.content ? `约${Math.max(1, Math.round(currentStep.content.length / 500))}分钟阅读` : '约1分钟阅读'}
									subtitle={activeChapter.intro ?? ''}
									title={currentStep.title}
								/>
							) : (
								<div className="bg-white rounded-2xl p-6 sm:p-10 shadow-sm border border-slate-200/60">
									<p className="text-slate-500 text-sm">{currentStep.title}</p>
								</div>
							)}

							{/* ── Practice button (show on last step) ── */}
							{isLastStep && activeChapter.description && (
								<div className="mt-6">
									<button
										className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-slate-200/60 text-sm font-medium text-slate-700 hover:border-slate-300 hover:shadow-sm transition-all"
										onClick={() => {
											const sessionId = toAcademySessionId(numericChapterId!);
											navigate(`/workplace/${sessionId}`, {
												state: {
													courseTitle: course?.title,
													chapterTitle: activeChapter?.title,
													description: activeChapter?.description,
												},
											});
										}}
										type="button"
									>
										<Play className="h-4 w-4" />
										在AI Office中演练
									</button>
								</div>
							)}

								{/* ── Step navigation ── */}
								<div className="mt-8 flex justify-end">
									<button
										className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
										onClick={handleGoToNextStep}
										type="button"
									>
										{isLastStep ? (nextChapter ? `完成并进入：${nextChapter.title}` : '完成本课') : '下一课'}
										<ArrowRight className="h-4 w-4" />
									</button>
								</div>

							{/* ── Course completed celebration ── */}
							{!nextChapter && isLastStep && activeChapter.learned && (
								<div className="mt-8 p-6 rounded-2xl border border-primary/20 bg-primary/5 text-center">
									<PartyPopper className="h-8 w-8 text-primary mx-auto mb-3" />
									<h3 className="text-lg font-bold text-primary mb-1">恭喜你完成全部课程！</h3>
									<p className="text-sm text-primary/80">你已学完本课程的所有章节，继续探索更多课程吧！</p>
									<Link
										className="inline-flex items-center gap-1.5 mt-4 px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
										to="/academy"
									>
										浏览更多课程
										<ChevronRight className="h-4 w-4" />
									</Link>
								</div>
							)}
						</>
					) : (
						<div className="flex flex-col items-center justify-center py-32 text-slate-400">
							<p className="text-sm">请在左侧选择一个章节开始学习</p>
						</div>
					)}
				</div>
			</main>
		</div>
	);
}
